import { AxiosInstance } from 'axios';
import { RPC_METHOD_TO_PATH } from '../../types/generated/rpcRoutes';
import type { RpcMethods } from '../../types/generated/rpcMethods';

export class RpcError extends Error {
  code: number;
  data?: unknown;

  constructor(code: number, message: string, data?: unknown) {
    super(message);
    this.name = 'RpcError';
    this.code = code;
    this.data = data;
  }
}

interface JsonRpcRequest {
  jsonrpc: '2.0';
  method: string;
  params?: Record<string, unknown>;
  id: number;
}

interface JsonRpcResponse<T = unknown> {
  jsonrpc: '2.0';
  id: number;
  result?: T;
  error?: { code: number; message: string; data?: unknown };
}

let nextId = 1;

function getDomainPath(method: string): string {
  const path = RPC_METHOD_TO_PATH[method];
  if (!path) throw new Error(`Unknown RPC method: ${method}`);
  return path;
}

// Typed overload for migrated methods present in the generated RpcMethods map.
export function rpcCall<M extends keyof RpcMethods>(
  client: AxiosInstance,
  method: M,
  params: RpcMethods[M]['params']
): Promise<RpcMethods[M]['result']>;
// Escape hatch for the not-yet-migrated call sites that pass an arbitrary method
// string and supply their own result type.
export function rpcCall<T>(
  client: AxiosInstance,
  method: string,
  params?: Record<string, unknown>
): Promise<T>;
export async function rpcCall<T>(
  client: AxiosInstance,
  method: string,
  params?: Record<string, unknown>
): Promise<T> {
  const path = getDomainPath(method);
  const id = nextId++;

  const request: JsonRpcRequest = {
    jsonrpc: '2.0',
    method,
    params: params || {},
    id,
  };

  const response = await client.post<JsonRpcResponse<T>>(path, request);
  const body = response.data;

  if (body.error) {
    throw new RpcError(body.error.code, body.error.message, body.error.data);
  }

  return body.result as T;
}

export interface BatchCall {
  method: string;
  params?: Record<string, unknown>;
}

export async function rpcBatch<T>(
  client: AxiosInstance,
  calls: BatchCall[]
): Promise<T[]> {
  if (calls.length === 0) return [];

  const path = getDomainPath(calls[0].method);
  const startId = nextId;
  nextId += calls.length;

  const requests: JsonRpcRequest[] = calls.map((call, i) => ({
    jsonrpc: '2.0' as const,
    method: call.method,
    params: call.params || {},
    id: startId + i,
  }));

  const response = await client.post<JsonRpcResponse<T>[]>(path, requests);
  const bodies = response.data;

  // Sort by id to match original request order
  const sorted = [...bodies].sort((a, b) => a.id - b.id);

  return sorted.map((body) => {
    if (body.error) {
      throw new RpcError(body.error.code, body.error.message, body.error.data);
    }
    return body.result as T;
  });
}
